import type { Event } from '../types';

export interface DemoPerson { id: number; name: string; role: 'student' | 'teacher'; badge: string; }
export interface DemoLecture {
  id: string; title: string; room: string; locationId: number; cameraId: number;
  start: string; end: string; teacherId: number; studentIds: number[];
}
export interface EducationDemo {
  companyId: number; schemaVersion: 1; date: string; timeZone: string;
  people: DemoPerson[]; lectures: DemoLecture[]; eventIds: number[]; locationIds: number[];
}
export type EducationConfiguration = Omit<EducationDemo, 'people'> & { people: Pick<DemoPerson, 'id' | 'role'>[] };
export function validateDemo(value: EducationConfiguration, company: number | null): EducationConfiguration {
  if (!value || value.companyId !== company || value.schemaVersion !== 1 || !value.people?.length || !value.lectures?.length) {
    throw new Error('Campus attendance is not configured for this account.');
  }
  new Intl.DateTimeFormat('en', { timeZone: value.timeZone }).format();
  if (!value.locationIds?.length || value.locationIds.some(id => !Number.isInteger(id) || id <= 0)) throw new Error('Invalid campus locations.');
  const ids = value.people.map(p => p.id);
  if (ids.some(id => !Number.isInteger(id) || id <= 0) || new Set(ids).size !== ids.length ||
    value.people.some(person => !['student', 'teacher'].includes(person.role))) throw new Error('Invalid education roster.');
  value.lectures.forEach(lecture => {
    const times = [lecture.start, lecture.end];
    if (!times.every(t => /(Z|[+-]\d\d:\d\d)$/.test(t) && Number.isFinite(Date.parse(t))) ||
      Date.parse(lecture.end) <= Date.parse(lecture.start) || lecture.cameraId <= 0 || lecture.locationId <= 0 ||
      !value.locationIds.includes(lecture.locationId) || !ids.includes(lecture.teacherId) || lecture.studentIds.some(id => !ids.includes(id))) throw new Error('Invalid lecture configuration.');
  });
  return value;
}
export function eventTime(value?: string): number {
  if (!value) return NaN;
  // The API contract returns normalized UTC; old naive UTC values are explicit here.
  return Date.parse(/(Z|[+-]\d\d:\d\d)$/.test(value) ? value : value.replace(' ', 'T') + 'Z');
}
export type EvidenceStatus = 'Classroom evidence' | 'Review required' | 'Seen on campus only' | 'No matching classroom evidence' | 'Data unavailable';
export function attendanceEvidence(personId: number, lecture: DemoLecture, events: Event[], complete: boolean) {
  if (!complete) return { status: 'Data unavailable' as EvidenceStatus, classroom: [] as Event[], campus: [] as Event[] };
  const start = Date.parse(lecture.start), end = Date.parse(lecture.end);
  const matchesPerson = (event: Event, confirmed: boolean) => event.registered_persons?.some(p =>
    p.person_id === personId && (confirmed ? p.review_status === 'confirmed' : p.review_status === 'pending'));
  const room = events.filter(event => Number(event.camera_id ?? event.event_camera) === lecture.cameraId &&
    event.event_location === lecture.locationId && eventTime(event.created_date) >= start && eventTime(event.created_date) < end);
  const classroom = room.filter(event => matchesPerson(event, true));
  const campus = events.filter(event => event.event_location === lecture.locationId && matchesPerson(event, true) &&
    eventTime(event.created_date) >= start - 2 * 60 * 60 * 1000 && eventTime(event.created_date) < end);
  const status: EvidenceStatus = classroom.length ? 'Classroom evidence' : room.some(event => matchesPerson(event, false)) ?
    'Review required' : campus.length ? 'Seen on campus only' : 'No matching classroom evidence';
  return { status, classroom, campus };
}
export function csvCell(value: string): string {
  const safe = /^\s*[=+@-]/.test(value) || /^[\t\r\n]/.test(value) ? "'" + value : value;
  return '"' + safe.replace(/"/g, '""') + '"';
}

export function vehicleObservations(events: Event[]) {
  const parse = (value: unknown): Record<string, unknown> => {
    try { const obj = typeof value === 'string' ? JSON.parse(value) : value; return obj && typeof obj === 'object' && !Array.isArray(obj) ? obj : {}; }
    catch { return {}; }
  };
  return events.flatMap(event => {
    const metadata = parse(event.ai_metadata), input = parse(event.event_metadata);
    const vehicles = Array.isArray(metadata.vehicles) ? metadata.vehicles : [];
    return vehicles.filter(vehicle => typeof vehicle?.license_plate === 'string' && vehicle.license_plate.trim()).map(vehicle => ({
      event, plate: String(vehicle.license_plate).trim().toUpperCase(),
      direction: input.direction === 'entry' ? 'Entry' : input.direction === 'exit' ? 'Exit' : 'Observation',
    }));
  }).sort((a, b) => eventTime(a.event.created_date) - eventTime(b.event.created_date));
}
